/**
 * Translation between the two filter-state shapes saved in a filter tab.
 *
 * `user_filter_tabs.tabs_json` is written by both apps and the server stores
 * `filterState` opaquely, so whatever an app puts there is what the other app
 * reads back. The Alpine app has always written its own camelCase, UI-shaped
 * keys — `leadCampaignIds`, `leadMinRevenue`, `leadHlrStatuses` — while the
 * React app works in the API's `LeadFilters` shape.
 *
 * Without translation an Alpine tab opens in React showing no filters at all,
 * and saving from React silently wipes that tab for Alpine. Both apps are live
 * on the same rows, so the fix has to be bidirectional: read either shape,
 * write both.
 *
 * This is deliberately the *only* hand-written mapping of API field names in
 * the app. It exists because the legacy shape is not in the OpenAPI spec and
 * never will be. Delete it when the Alpine leads page is retired.
 */
import type { LeadFilters } from './queries';

/** The Alpine app's saved shape. Its keys are UI state, not API fields. */
interface LegacyFilterState {
  leadDateRange?: string;
  leadCustomDateFrom?: string;
  leadCustomDateTo?: string;
  leadCampaignIds?: string[];
  leadDeliveryStatuses?: string[];
  leadSoldStatuses?: string[];
  leadSource?: string;
  leadSupplier?: string;
  leadBuyer?: string;
  leadAttemptedBuyer?: string;
  leadMinRevenue?: string | number;
  leadMaxRevenue?: string | number;
  leadMethod?: string;
  leadImportIds?: string[];
  leadHlrStatuses?: string[];
  fieldFilters?: {
    field?: string;
    operator?: string;
    value?: string;
    allCampaigns?: boolean;
  }[];
  /** Column visibility. React does not use it yet, but must not drop it. */
  leadSelectedColumns?: string[];
  showLeadAdvanced?: boolean;
}

/**
 * Every key of the API shape. A `Record` over all of `LeadFilters`' keys, so
 * a field added to the server's search body fails the build here until it is
 * listed, rather than being dropped from saved tabs without a sound.
 *
 * `any_of` (OR groups), `ids` (named records), `repeats_min` (people with
 * more than one submission) and `owner` (whose people) are left out on
 * purpose: they belong to one search from the command bar or the people
 * view, and the filter panel a segment opens in cannot show them, so a
 * saved tab never carries them.
 */
const NATIVE_KEYS: Record<Exclude<keyof LeadFilters, 'any_of' | 'ids' | 'repeats_min' | 'owner'>, true> = {
  attempted_buyer: true,
  buyer: true,
  buyer_ids: true,
  campaign_id: true,
  campaign_ids: true,
  capture_methods: true,
  date_from: true,
  date_range: true,
  date_to: true,
  delivery_statuses: true,
  destination_ids: true,
  field_filters: true,
  hlr_statuses: true,
  ignore_campaign_filter: true,
  import_ids: true,
  lead_activities: true,
  lead_method: true,
  max_value: true,
  min_value: true,
  // Score (internal: the filter panel offers it only to members who see scores).
  max_score: true,
  min_score: true,
  score_band: true,
  sort: true,
  page: true,
  page_size: true,
  search: true,
  sold_statuses: true,
  source: true,
  status: true,
  supplier: true,
  supplier_ids: true,
};

const isNativeKey = (k: string): boolean => Object.prototype.hasOwnProperty.call(NATIVE_KEYS, k);

const str = (v: unknown): string | null => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t === '' ? null : t;
};
const list = (v: unknown): string[] | null =>
  Array.isArray(v) && v.length > 0 ? (v as string[]) : null;
const num = (v: unknown): number | null => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Read a saved `filterState` in either shape.
 *
 * A tab saved by React already has the API shape, so the legacy keys are
 * simply absent and every lookup yields null.
 */
export function toFilters(state: unknown): LeadFilters {
  if (!state || typeof state !== 'object') return {};
  const s = state as LegacyFilterState & LeadFilters;

  // Already the API shape: keep only the API keys. Any one of them is enough
  // to tell, since a tab saved with nothing but a supplier picked carries
  // `supplier_ids` and none of the older keys.
  const looksNative = Object.keys(s).some(isNativeKey);
  if (looksNative) {
    return Object.fromEntries(
      Object.entries(s).filter(([k]) => isNativeKey(k)),
    ) as LeadFilters;
  }

  const fieldFilters = (s.fieldFilters ?? [])
    // The Alpine builder seeds a blank row; an empty field name is not a filter.
    .filter((f) => (f.field ?? '').trim() !== '')
    .map((f) => ({
      field: (f.field ?? '').trim(),
      operator: f.operator || 'equals',
      value: f.value ?? '',
    }));

  return {
    date_range: str(s.leadDateRange),
    date_from: str(s.leadCustomDateFrom),
    date_to: str(s.leadCustomDateTo),
    campaign_ids: list(s.leadCampaignIds),
    delivery_statuses: list(s.leadDeliveryStatuses),
    sold_statuses: list(s.leadSoldStatuses),
    source: str(s.leadSource),
    supplier: str(s.leadSupplier),
    buyer: str(s.leadBuyer),
    attempted_buyer: str(s.leadAttemptedBuyer),
    min_value: num(s.leadMinRevenue),
    max_value: num(s.leadMaxRevenue),
    lead_method: str(s.leadMethod),
    import_ids: list(s.leadImportIds),
    hlr_statuses: list(s.leadHlrStatuses),
    field_filters: fieldFilters.length ? fieldFilters : null,
    // Alpine marks cross-campaign search per condition; the API has one flag.
    ignore_campaign_filter: (s.fieldFilters ?? []).some((f) => f.allCampaigns) || null,
  };
}

/**
 * Write both shapes into one `filterState`, so a tab saved in React still
 * opens correctly in the Alpine app.
 *
 * `previous` carries through the keys React has no concept of — column
 * selection, for one — rather than dropping settings the other app relies on.
 */
export function toStoredState(filters: LeadFilters, previous?: unknown): unknown {
  const prev = (previous && typeof previous === 'object' ? previous : {}) as LegacyFilterState;

  const legacy: LegacyFilterState = {
    leadDateRange: filters.date_range ?? '',
    leadCustomDateFrom: filters.date_from ?? '',
    leadCustomDateTo: filters.date_to ?? '',
    leadCampaignIds: filters.campaign_ids ?? [],
    leadDeliveryStatuses: filters.delivery_statuses ?? [],
    leadSoldStatuses: filters.sold_statuses ?? [],
    leadSource: filters.source ?? '',
    leadSupplier: filters.supplier ?? '',
    leadBuyer: filters.buyer ?? '',
    leadAttemptedBuyer: filters.attempted_buyer ?? '',
    leadMinRevenue: filters.min_value ?? '',
    leadMaxRevenue: filters.max_value ?? '',
    leadMethod: filters.lead_method ?? '',
    leadImportIds: filters.import_ids ?? [],
    leadHlrStatuses: filters.hlr_statuses ?? [],
    fieldFilters: (filters.field_filters ?? []).map((f, i) => ({
      id: i + 1,
      field: f.field,
      operator: f.operator,
      value: f.value ?? '',
      allCampaigns: filters.ignore_campaign_filter ?? false,
    })),
    // Preserved, not authored: React has no column picker yet.
    ...(prev.leadSelectedColumns ? { leadSelectedColumns: prev.leadSelectedColumns } : {}),
    ...(prev.showLeadAdvanced != null ? { showLeadAdvanced: prev.showLeadAdvanced } : {}),
  };

  // Both shapes in one object. The keys do not collide, and each app reads the
  // half it understands.
  return { ...legacy, ...filters };
}
